import { fillDecoys } from '../authoring';
/**
 * UGC 클라이언트 (ADR-048 · RULES §1.19) — 투고 · 내 투고 · 별 상위 · 별 주기.
 * 검증은 보내기 전에 엔진의 `questionRuleIssues`(규칙 4~14)로 한다 — 서비스는 구조 규칙만 다시 본다.
 * 네트워크는 `fetchJson` 으로 주입한다(테스트는 가짜).
 */
import { type Alphabet, LOCALES, type Locale } from '../engine/alphabet';
import { type PackIssue, questionRuleIssues } from '../engine/pack';

export interface UgcSubmission {
  readonly id: string;
  readonly q: string;
  readonly a: string;
  readonly status: 'pending' | 'approved' | 'rejected';
  readonly note: string;
  readonly questionId: string | null;
  readonly createdAt: string;
}

export interface TopJoke {
  readonly questionId: string;
  readonly q: string;
  readonly votes: number;
  readonly mine: boolean;
}

export type UgcFetch = (
  url: string,
  init?: { method?: string; body?: unknown; timeoutMs?: number },
) => Promise<{ ok: boolean; status: number; json: unknown }>;

/**
 * 실패 종류 — 화면은 이것만 보고 UI 문자열을 고른다(RULES §1.19 UGC_AVAILABILITY).
 * HTTP 상태와 서비스 원문(한국어 고정)은 사람에게 보이지 않는다 — message 는 진단용이다.
 */
export type UgcErrorKind = 'offline' | 'unavailable' | 'invalid' | 'duplicate' | 'limit';

export class UgcError extends Error {
  constructor(
    readonly status: number,
    readonly kind: UgcErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'UgcError';
  }
}

/** 투고 화면을 열 때 서비스를 확인하는 상한 — 첫 화면의 팩 요청과 같은 값(PACK_FETCH_TIMEOUT_MS) */
export const UGC_PING_TIMEOUT_MS = 2500;

/**
 * HTTP 상태 → 실패 종류. 0 = 연결 실패 · 시간 초과. 404 · 405 · 5xx 는 서비스가 없거나 죽은 것이다 —
 * 게임 정적 파일만 먼저 배포되면 nginx 가 /ugc 에 404(GET) · 405(POST)를 준다.
 */
export function ugcErrorKind(status: number): UgcErrorKind {
  if (status === 0) return 'offline';
  if (status === 400 || status === 422) return 'invalid';
  if (status === 409) return 'duplicate';
  if (status === 429) return 'limit';
  return 'unavailable';
}

const UID_RE = /^[A-Za-z0-9_-]{4,64}$/;

/** 투고 전 검증 — 정답을 넣으면 오답을 채우고(alphabet), 규칙 4~14 를 본다. 문제 id 는 임시 c001 */
export function prepareSubmission(
  q: string,
  a: string,
  locale: Locale,
): {
  readonly q: string;
  readonly a: string;
  readonly d: readonly string[];
  readonly issues: readonly PackIssue[];
} {
  const prof = LOCALES[locale];
  const alphabet: Alphabet = prof.alphabet;
  const qq = q.trim();
  const aa = locale === 'en' ? a.trim().toUpperCase() : a.trim();
  const d = prof.answerRe.test(aa) ? fillDecoys(aa, 6, alphabet) : [];
  const issues = questionRuleIssues({ id: 'c001', q: qq, a: aa, d }, { kind: 'joke', locale });
  return { q: qq, a: aa, d, issues };
}

export function createUgcClient(origin: string, fetchJson: UgcFetch) {
  const url = (path: string): string => `${origin}/ugc${path}`;
  const unwrap = <T>(res: { ok: boolean; status: number; json: unknown }): T => {
    // 200 인데 JSON 객체가 아니면 정적 서버의 index.html 폴백이다 — 서비스가 없는 것과 같다
    if (res.ok && typeof res.json === 'object' && res.json !== null) return res.json as T;
    const e = res.json as { error?: unknown } | null;
    const detail = typeof e?.error === 'string' ? e.error : `HTTP ${res.status}`;
    throw new UgcError(res.status, res.ok ? 'unavailable' : ugcErrorKind(res.status), detail);
  };
  return {
    async submit(
      uid: string,
      locale: Locale,
      q: string,
      a: string,
      d: readonly string[],
    ): Promise<UgcSubmission> {
      if (!UID_RE.test(uid)) throw new UgcError(400, 'invalid', 'uid');
      const r = unwrap<{ submission: UgcSubmission }>(
        await fetchJson(url('/submissions'), { method: 'POST', body: { uid, locale, q, a, d } }),
      );
      return r.submission;
    },
    async mine(uid: string): Promise<UgcSubmission[]> {
      return unwrap<{ submissions: UgcSubmission[] }>(
        await fetchJson(url(`/mine?uid=${encodeURIComponent(uid)}`)),
      ).submissions;
    },
    async top(locale: Locale, uid: string | null, limit = 50): Promise<TopJoke[]> {
      const q = `?locale=${locale}&limit=${limit}${uid === null ? '' : `&uid=${encodeURIComponent(uid)}`}`;
      return unwrap<{ top: TopJoke[] }>(await fetchJson(url(`/top${q}`))).top;
    },
    /**
     * 서비스가 투고를 받을 수 있는가 — 투고 화면을 열 때 짧게 본다. **식별자를 싣지 않는다**(상위 1건 · uid 없음).
     * 연결 실패 · 404 · 405 · HTML 폴백이면 false. 던지지 않는다.
     */
    async available(locale: Locale, timeoutMs = UGC_PING_TIMEOUT_MS): Promise<boolean> {
      try {
        const res = await fetchJson(url(`/top?locale=${locale}&limit=1`), { timeoutMs });
        return (
          res.ok &&
          typeof res.json === 'object' &&
          res.json !== null &&
          Array.isArray((res.json as { top?: unknown }).top)
        );
      } catch {
        return false;
      }
    },
    /** 판의 슬롯(c001…)으로 별을 준다 — 서비스가 발행본의 source_ids 로 풀 문제를 찾는다 */
    async vote(
      uid: string,
      packId: string,
      version: string,
      slot: string,
      on: boolean,
    ): Promise<{ questionId: string; votes: number; mine: boolean }> {
      return unwrap(
        await fetchJson(url('/votes'), {
          method: on ? 'POST' : 'DELETE',
          body: { uid, packId, version, slot },
        }),
      );
    },
  };
}

export type UgcClient = ReturnType<typeof createUgcClient>;

/** 브라우저 fetch — JSON 본문 · 8초 상한(가용성 확인은 더 짧게) · 실패는 ok:false 로 (게임을 막지 않는다) */
export function browserUgcFetch(): UgcFetch {
  return async (u, init) => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), init?.timeoutMs ?? 8000);
    try {
      const req: RequestInit = { method: init?.method ?? 'GET', signal: ctl.signal };
      if (init?.body !== undefined) {
        req.headers = { 'Content-Type': 'application/json' };
        req.body = JSON.stringify(init.body);
      }
      const res = await fetch(u, req);
      const text = await res.text();
      let json: unknown = null;
      try {
        json = text === '' ? null : JSON.parse(text);
      } catch {
        json = null;
      }
      return { ok: res.ok, status: res.status, json };
    } catch {
      return { ok: false, status: 0, json: { error: 'offline' } };
    } finally {
      clearTimeout(timer);
    }
  };
}
